import React, { useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useChipColors } from '@/hooks/useChipColors';
import { Button, Card } from '@/components/ui';
import type { ChipColorEntry } from '@/lib/types';

const DEFAULT_COLORS: Omit<ChipColorEntry, 'id'>[] = [
  { label: 'White', hex: '#FFFFFF', value: 1, tolerance: 20 },
  { label: 'Red', hex: '#FF0000', value: 5, tolerance: 20 },
  { label: 'Blue', hex: '#0066FF', value: 10, tolerance: 20 },
  { label: 'Green', hex: '#00CC00', value: 25, tolerance: 20 },
  { label: 'Black', hex: '#000000', value: 100, tolerance: 20 },
];

export function ChipColorConfig() {
  const { chipColors } = useChipColors();
  const [colors, setColors] = useState<ChipColorEntry[]>(
    chipColors.length > 0 ? chipColors : DEFAULT_COLORS.map(c => ({ ...c, id: crypto.randomUUID() }))
  );
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const handleAdd = () => {
    setColors(prev => [...prev, { id: crypto.randomUUID(), label: '', hex: '#CCCCCC', value: 0, tolerance: 20 }]);
  };

  const handleRemove = (id: string) => {
    setColors(prev => prev.filter(c => c.id !== id));
  };

  const handleUpdate = (id: string, field: keyof ChipColorEntry, val: string | number) => {
    setColors(prev => prev.map(c => c.id === id ? { ...c, [field]: val } : c));
  };

  const handleSave = async () => {
    const valid = colors.filter(c => c.label.trim() && c.value > 0);
    if (valid.length === 0) {
      alert('Add at least one color with a label and value.');
      return;
    }
    setSaving(true);
    try {
      await setDoc(doc(db, 'config', 'app'), { chip_colors: valid }, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-sm font-semibold text-[#2c2f30]">Chip Colors</h2>
        <button onClick={handleAdd} className="text-xs font-semibold text-[#006855] hover:text-[#006855]/80 transition-colors">
          + Add
        </button>
      </div>

      <p className="text-xs text-[#6b7280] mb-4">
        Define the color→value mapping for chip detection. The scanner matches pixels within the hue tolerance range and counts contiguous blobs. Adjust tolerance if chips aren't detected or false positives appear.
      </p>

      {colors.length === 0 ? (
        <div className="text-[#6b7280] text-sm text-center py-4">
          No chip colors configured. Add at least one to use the scanner.
        </div>
      ) : (
        <div className="space-y-4 mb-4">
          {colors.map((color, idx) => (
            <div key={color.id} className={`pb-3 ${idx < colors.length - 1 ? 'border-b border-gray-100' : ''}`}>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="color"
                  value={color.hex}
                  onChange={e => handleUpdate(color.id, 'hex', e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border border-gray-200 flex-shrink-0"
                />
                <input
                  type="text"
                  value={color.label}
                  onChange={e => handleUpdate(color.id, 'label', e.target.value)}
                  placeholder="Color name"
                  className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-white p-2 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
                />
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className="text-xs text-[#6b7280]">$</span>
                  <input
                    type="number"
                    value={color.value}
                    onChange={e => handleUpdate(color.id, 'value', Number(e.target.value))}
                    min={0}
                    step={0.5}
                    className="w-20 rounded-lg border border-gray-200 bg-white p-2 text-sm font-medium text-right focus:outline-none focus:ring-2 focus:ring-[#006855]/20 focus:border-[#006855]"
                  />
                </div>
                <button
                  onClick={() => handleRemove(color.id)}
                  className="text-xs text-red-500 hover:text-red-700 transition-colors flex-shrink-0"
                >
                  ✕
                </button>
              </div>
              <div className="flex items-center gap-2 ml-10">
                <label className="text-xs text-[#6b7280]">Tolerance:</label>
                <input
                  type="range"
                  min={5}
                  max={60}
                  value={color.tolerance}
                  onChange={e => handleUpdate(color.id, 'tolerance', Number(e.target.value))}
                  className="flex-1 h-1.5 rounded-full appearance-none bg-gray-200 accent-[#006855]"
                />
                <span className="text-xs text-[#2c2f30] font-medium w-8 text-right">{color.tolerance}°</span>
              </div>
            </div>
          ))}
        </div>
      )}

      <Button fullWidth onClick={handleSave} disabled={saving || colors.length === 0}>
        {saving ? 'Saving...' : saved ? 'Saved!' : 'Save Chip Colors'}
      </Button>
    </Card>
  );
}
